import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    CITIES,
    adminListApplications,
    adminPatchApplication,
    adminDownloadApplicationsCSV,
    fetchEmailEvents,
    sendCustomEmail,
} from "@/lib/api";
import {
    EVENT_META,
    Field,
    PageHeader,
    Select,
    StatCell,
    STATUSES,
    StatusPill,
    fmtDate,
    fmtTime,
    prettyTpl,
} from "../_shared";

export default function ApplicationsTab() {
    const [data, setData] = useState({
        items: [],
        total: 0,
        counts_by_status: {},
        counts_by_city: {},
    });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [city, setCity] = useState("all");
    const [status, setStatus] = useState("all");
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(null);
    const debounceRef = useRef();

    const load = useCallback(
        async (override = {}) => {
            setLoading(true);
            try {
                const params = {
                    q: override.q ?? q,
                    city: override.city ?? city,
                    status: override.status ?? status,
                    page: override.page ?? page,
                    page_size: 50,
                };
                const res = await adminListApplications(params);
                setData(res);
            } catch (e) {
                toast(
                    e?.response?.data?.detail ||
                        "Could not load applications.",
                );
            } finally {
                setLoading(false);
            }
        },
        [q, city, status, page],
    );

    useEffect(() => {
        load({ page: 1 });
        setPage(1);
        // eslint-disable-next-line
    }, [city, status]);

    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            load({ page: 1 });
            setPage(1);
        }, 300);
        return () => clearTimeout(debounceRef.current);
        // eslint-disable-next-line
    }, [q]);

    const exportCsv = async () => {
        try {
            await adminDownloadApplicationsCSV({ q, city, status });
            toast("CSV exported.");
        } catch (e) {
            toast(e?.response?.data?.detail || "Export failed.");
        }
    };

    const totalAll = useMemo(
        () => Object.values(data.counts_by_status).reduce((a, b) => a + b, 0),
        [data.counts_by_status],
    );

    const pageCount = Math.max(1, Math.ceil(data.total / 50));

    return (
        <div data-testid="applications-tab">
            <PageHeader
                eyebrow="Inbox"
                title="Applications"
                description="Every founder who's pitched themselves for a city slot."
                actions={
                    <button
                        type="button"
                        onClick={exportCsv}
                        data-testid="app-export-csv"
                        className="vc-btn vc-btn-ghost"
                    >
                        Export CSV ↓
                    </button>
                }
            />

            {/* Stats banner */}
            <div className="grid grid-cols-2 md:grid-cols-7 gap-px bg-line border border-line rounded-2xl overflow-hidden mb-8">
                <StatCell label="Total" value={totalAll} accent />
                {STATUSES.slice(1).map((s) => (
                    <StatCell
                        key={s.key}
                        label={s.label}
                        value={data.counts_by_status[s.key] || 0}
                    />
                ))}
            </div>

            {/* Controls */}
            <div className="grid lg:grid-cols-12 gap-3 mb-6">
                <div className="lg:col-span-5">
                    <input
                        data-testid="app-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search name, email, ref ID, motivation…"
                        className="w-full bg-white/[0.04] border border-line focus:border-[#5fe3c9] rounded-full px-5 py-3 outline-none font-body text-ink placeholder:text-ink-soft/60"
                    />
                </div>
                <div className="lg:col-span-3">
                    <Select
                        value={city}
                        onChange={setCity}
                        options={[
                            { value: "all", label: "All cities" },
                            ...CITIES.map((c) => ({
                                value: c,
                                label: `${c}${data.counts_by_city[c] ? ` · ${data.counts_by_city[c]}` : ""}`,
                            })),
                        ]}
                        testId="app-city-filter"
                    />
                </div>
                <div className="lg:col-span-4 flex items-center justify-end gap-2 flex-wrap">
                    {STATUSES.map((s) => (
                        <button
                            key={s.key}
                            type="button"
                            data-testid={`app-status-${s.key}`}
                            onClick={() => setStatus(s.key)}
                            className={`px-3 py-2 rounded-full font-display font-bold uppercase tracking-[0.15em] text-[0.6rem] transition-colors border ${
                                status === s.key
                                    ? "bg-[#f4f4f0] text-[#0a0a0a] border-[#f4f4f0]"
                                    : "text-ink-soft border-line hover:text-ink hover:border-ink"
                            }`}
                        >
                            {s.label}
                            {s.key !== "all" &&
                                data.counts_by_status[s.key] != null && (
                                    <span className="ml-1 opacity-60">
                                        {data.counts_by_status[s.key] || 0}
                                    </span>
                                )}
                        </button>
                    ))}
                </div>
            </div>

            <p className="vc-section-label text-ink-soft mb-3">
                {loading ? "Loading…" : `${data.total} results`}
            </p>

            <div
                data-testid="applications-table"
                className="border border-line rounded-2xl overflow-hidden"
            >
                <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-white/[0.04] border-b border-line vc-section-label text-ink-soft">
                    <div className="col-span-3">Applicant</div>
                    <div className="col-span-2">City</div>
                    <div className="col-span-2">Ref</div>
                    <div className="col-span-2">Submitted</div>
                    <div className="col-span-3">Status</div>
                </div>
                {data.items.length === 0 && !loading && (
                    <div className="px-5 py-10 text-ink-soft">
                        No applications match your filter.
                    </div>
                )}
                {data.items.map((a) => (
                    <button
                        key={a.reference_id}
                        type="button"
                        onClick={() => setSelected(a.reference_id)}
                        data-testid={`app-row-${a.reference_id}`}
                        className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-4 px-5 py-4 border-b border-line last:border-0 hover:bg-white/[0.03] transition-colors items-center"
                    >
                        <div className="md:col-span-3">
                            <div className="font-display font-bold text-base">
                                {a.full_name}
                            </div>
                            <div className="text-ink-soft text-xs truncate">
                                {a.email}
                            </div>
                        </div>
                        <div className="md:col-span-2 text-sm text-ink">
                            {a.city}
                        </div>
                        <div className="md:col-span-2 font-mono text-sm text-ink">
                            {a.reference_id}
                        </div>
                        <div className="md:col-span-2 text-sm text-ink-soft">
                            {fmtDate(a.created_at)}
                        </div>
                        <div className="md:col-span-3">
                            <StatusPill status={a.status} />
                        </div>
                    </button>
                ))}
            </div>

            {pageCount > 1 && (
                <div className="flex items-center justify-center gap-3 mt-8">
                    <button
                        type="button"
                        disabled={page <= 1}
                        onClick={() => {
                            const p = page - 1;
                            setPage(p);
                            load({ page: p });
                        }}
                        className="vc-btn vc-btn-ghost disabled:opacity-30"
                    >
                        ← Prev
                    </button>
                    <span className="vc-section-label text-ink-soft">
                        Page {page} / {pageCount}
                    </span>
                    <button
                        type="button"
                        disabled={page >= pageCount}
                        onClick={() => {
                            const p = page + 1;
                            setPage(p);
                            load({ page: p });
                        }}
                        className="vc-btn vc-btn-ghost disabled:opacity-30"
                    >
                        Next →
                    </button>
                </div>
            )}

            {selected && (
                <ApplicationDrawer
                    refId={selected}
                    onClose={() => setSelected(null)}
                    onUpdated={() => load()}
                />
            )}
        </div>
    );
}

// --------------------------------------------------------------------------- //
// Drawer                                                                       //
// --------------------------------------------------------------------------- //

function ApplicationDrawer({ refId, onClose, onUpdated }) {
    const [app, setApp] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notes, setNotes] = useState("");
    const [status, setStatus] = useState("received");
    const [loading, setLoading] = useState(true);
    const [showCompose, setShowCompose] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        adminListApplications({ q: refId, page_size: 1 })
            .then((r) => {
                if (!alive) return;
                const a = r.items[0];
                if (a) {
                    setApp(a);
                    setNotes(a.admin_notes || "");
                    setStatus(a.status || "received");
                }
            })
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [refId]);

    const save = async () => {
        setBusy(true);
        try {
            const updated = await adminPatchApplication(refId, {
                status,
                admin_notes: notes,
            });
            setApp(updated);
            toast("Application updated.");
            onUpdated?.();
        } catch (e) {
            toast(e?.response?.data?.detail || "Save failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div
                aria-hidden
                onClick={onClose}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
            />
            <aside
                data-testid="application-drawer"
                className="fixed top-0 right-0 h-full w-full sm:w-[560px] bg-[#0a0a0a] border-l border-line z-[70] overflow-y-auto shadow-[-12px_0_40px_rgba(0,0,0,0.5)]"
            >
                <div className="sticky top-0 z-10 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-line px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="vc-section-label text-[#5fe3c9]">
                            Application
                        </div>
                        <div className="font-mono text-lg font-bold">
                            {refId}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="drawer-close"
                        className="text-ink-soft hover:text-ink text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {loading && <div className="p-6 text-ink-soft">Loading…</div>}

                {!loading && app && (
                    <div className="p-6 space-y-7">
                        <div>
                            <div className="vc-section-label text-ink-soft mb-2">
                                Status
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {STATUSES.slice(1).map((s) => (
                                    <button
                                        key={s.key}
                                        type="button"
                                        onClick={() => setStatus(s.key)}
                                        data-testid={`drawer-status-${s.key}`}
                                        className={`px-3 py-2 rounded-full font-display font-bold uppercase tracking-[0.15em] text-[0.65rem] border transition-colors ${
                                            status === s.key
                                                ? "bg-[#f4f4f0] text-[#0a0a0a] border-[#f4f4f0]"
                                                : "text-ink-soft border-line hover:border-ink hover:text-ink"
                                        }`}
                                    >
                                        {s.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="vc-section-label text-ink-soft mb-2">
                                Admin notes
                            </div>
                            <textarea
                                data-testid="drawer-notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={4}
                                placeholder="Private notes only visible to admins…"
                                className="w-full bg-white/[0.03] border border-line focus:border-[#5fe3c9] rounded-2xl p-4 text-ink outline-none placeholder:text-ink-soft/50"
                            />
                        </div>

                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={save}
                                disabled={busy}
                                data-testid="drawer-save"
                                className="vc-btn vc-btn-accent flex-1 justify-center disabled:opacity-60"
                            >
                                {busy ? "Saving…" : "Save changes"}
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowCompose(true)}
                                data-testid="drawer-send-email"
                                className="vc-btn vc-btn-ghost"
                                title="Send a custom email to this applicant"
                            >
                                ✉ Email
                            </button>
                        </div>

                        <div className="vc-rule" />

                        <EmailTimeline refId={refId} status={status} />

                        <div className="vc-rule" />

                        <DetailRow label="Name" value={app.full_name} />
                        <DetailRow label="Email" value={app.email} mono />
                        <DetailRow label="Based in" value={app.city} />
                        {app.current_location && (
                            <DetailRow
                                label="Also lives in"
                                value={app.current_location}
                            />
                        )}
                        <DetailRow
                            label="Submitted"
                            value={fmtDate(app.created_at)}
                        />
                        {app.twitter && (
                            <DetailRow label="Twitter" value={app.twitter} />
                        )}
                        {app.linkedin && (
                            <DetailRow
                                label="LinkedIn"
                                value={app.linkedin}
                                link
                            />
                        )}
                        {app.project_url && (
                            <DetailRow
                                label="Project"
                                value={app.project_url}
                                link
                            />
                        )}
                        <DetailRow
                            label="Organised before"
                            value={app.organised_before ? "Yes" : "No"}
                        />
                        {app.organised_before && app.organised_details && (
                            <DetailLong
                                label="What they ran"
                                value={app.organised_details}
                            />
                        )}
                        {app.who_youd_bring && (
                            <DetailLong
                                label="Who they'd bring"
                                value={app.who_youd_bring}
                            />
                        )}
                        {app.why_you && (
                            <DetailLong
                                label="Why them, why now"
                                value={app.why_you}
                            />
                        )}
                        {app.extra && (
                            <DetailLong
                                label="Anything else"
                                value={app.extra}
                            />
                        )}
                    </div>
                )}
            </aside>
            {showCompose && app && (
                <QuickComposeModal
                    refId={refId}
                    toEmail={app.email}
                    toName={app.full_name}
                    onClose={() => setShowCompose(false)}
                />
            )}
        </>
    );
}

function QuickComposeModal({ refId, toEmail, toName, onClose }) {
    const [subject, setSubject] = useState("");
    const [html, setHtml] = useState("");
    const [busy, setBusy] = useState(false);

    const onSend = async () => {
        if (!subject.trim() || !html.trim()) {
            toast("Subject and body are required.");
            return;
        }
        setBusy(true);
        try {
            await sendCustomEmail({
                reference_id: refId,
                subject: subject.trim(),
                html,
            });
            toast(`Email queued to ${toEmail}.`);
            onClose();
        } catch (e) {
            toast(e?.response?.data?.detail || "Send failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4"
            onClick={onClose}
            data-testid="quick-compose-modal"
        >
            <div
                className="relative w-full max-w-2xl bg-background border border-line rounded-2xl p-6 md:p-8 space-y-5"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="vc-section-label text-[#5fe3c9] mb-1">
                            Custom email
                        </div>
                        <h2 className="font-display font-bold text-xl">
                            To {toName}{" "}
                            <span className="text-ink-soft text-base font-normal">
                                · {toEmail}
                            </span>
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-ink-soft hover:text-ink text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>
                <Field label="Subject">
                    <input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        data-testid="quick-compose-subject"
                        placeholder="A note about your Vibecity application"
                        className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                    />
                </Field>
                <Field label="HTML body — supports {{first_name}}, {{city}}, {{reference_id}}">
                    <textarea
                        value={html}
                        onChange={(e) => setHtml(e.target.value)}
                        data-testid="quick-compose-html"
                        rows={12}
                        placeholder={`<p>Hi {{first_name}},</p>\n<p>Quick note about your application for Vibecity {{city}}…</p>\n<p>— Maddy</p>`}
                        className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40 text-xs font-mono resize-none"
                    />
                </Field>
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={onSend}
                        disabled={busy}
                        data-testid="quick-compose-send"
                        className="vc-btn vc-btn-accent flex-1 justify-center disabled:opacity-60"
                    >
                        {busy ? "Sending…" : "Send"}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="vc-btn vc-btn-ghost"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

function DetailRow({ label, value, link, mono }) {
    return (
        <div>
            <div className="vc-section-label text-ink-soft mb-1">{label}</div>
            {link ? (
                <a
                    href={value}
                    target="_blank"
                    rel="noreferrer"
                    className={`text-ink hover:text-gradient break-all ${mono ? "font-mono" : ""}`}
                >
                    {value}
                </a>
            ) : (
                <div
                    className={`text-ink break-all ${mono ? "font-mono" : ""}`}
                >
                    {value}
                </div>
            )}
        </div>
    );
}

function DetailLong({ label, value }) {
    return (
        <div>
            <div className="vc-section-label text-ink-soft mb-2">{label}</div>
            <p className="text-ink leading-relaxed whitespace-pre-wrap text-[0.95rem] bg-white/[0.03] border border-line rounded-2xl p-4">
                {value}
            </p>
        </div>
    );
}

function EmailTimeline({ refId, status }) {
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const r = await fetchEmailEvents(refId);
            setItems(r.items || []);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Failed to load email events.");
        } finally {
            setLoading(false);
        }
    }, [refId]);

    useEffect(() => {
        load();
    }, [load, status]);

    return (
        <div data-testid="email-timeline">
            <div className="flex items-center justify-between mb-3">
                <div className="vc-section-label text-ink-soft">
                    Email timeline
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="text-xs text-ink-soft hover:text-ink transition-colors uppercase tracking-[0.18em]"
                    data-testid="email-timeline-refresh"
                >
                    {loading ? "…" : "Refresh"}
                </button>
            </div>

            {err && <div className="text-xs text-[#ff6b6b] mb-3">{err}</div>}

            {!loading && items && items.length === 0 && (
                <div
                    className="text-sm text-ink-soft bg-white/[0.03] border border-line rounded-2xl p-4"
                    data-testid="email-timeline-empty"
                >
                    No email events yet. Resend events will appear once the
                    webhook fires.
                </div>
            )}

            {items && items.length > 0 && (
                <ol className="relative border-l border-line pl-5 ml-1 space-y-4">
                    {items.map((ev, i) => {
                        const meta =
                            EVENT_META[ev.event_type] || {
                                label: ev.event_type,
                                color: "#9d9d96",
                                dot: "#9d9d96",
                            };
                        return (
                            <li
                                key={`${ev.email_id}-${ev.event_type}-${i}`}
                                className="relative"
                                data-testid={`email-event-${i}`}
                            >
                                <span
                                    aria-hidden
                                    style={{
                                        position: "absolute",
                                        left: -27,
                                        top: 5,
                                        width: 10,
                                        height: 10,
                                        borderRadius: 999,
                                        background: meta.dot,
                                        boxShadow: `0 0 0 3px #0a0a0a, 0 0 12px ${meta.dot}66`,
                                    }}
                                />
                                <div
                                    className="flex items-baseline justify-between gap-3"
                                    style={{ color: meta.color }}
                                >
                                    <span className="text-sm font-bold uppercase tracking-[0.14em]">
                                        {meta.label}
                                    </span>
                                    <span className="text-[11px] text-ink-soft uppercase tracking-[0.12em]">
                                        {fmtTime(ev.received_at)}
                                    </span>
                                </div>
                                {ev.template && (
                                    <div className="text-[12px] text-ink-soft mt-1">
                                        Template: {prettyTpl(ev.template)}
                                    </div>
                                )}
                                {ev.click_url && (
                                    <a
                                        href={ev.click_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="block text-[12px] text-ink hover:text-gradient mt-1 break-all"
                                    >
                                        ↗ {ev.click_url}
                                    </a>
                                )}
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
}
